import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Alert, AlertIcon } from '@chakra-ui/react';
import { useRequestConsultMutation, useRegisterUserToClinicMutation, useRegisterFamilyMemberToClinicMutation, useBannedPatientsLazyQuery } from '../graphql/types-and-hooks';
import { useBookAppointmentStore } from '../store';
import { isAgeInRange } from '../utils/isAgeInRange';
import { useAlert } from './AlertProvider';
import { useAppointmentBooking } from '../hooks/useAppointmentBooking';
import { AppointmentDetails } from './ConformationStep/AppointmentDetails';
import { PaymentConfirmation } from './ConformationStep/PaymentConfirmation';
import BookingSuccess from './BookingSuccess';
import PaymentStep from './PaymentStep';
import { patientStore } from '../store/PatientStore';
import { doctorsStore } from '../store/DoctorsStore';
import { appointmentTypesStore } from '../store/AppointmentTypesStore';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { bookingFlowStore } from '../store/BookingFlowStore';
import { calculateAgeFromDOB } from '../utils/dates';
import { logError } from '../components/logger';
import logger from '../utils/logger';
import { clinicStore } from '../store/ClinicStore';
import { toISOString } from '../lib/util';

interface ConfirmationStepProps {
  onClose: () => void;
  selectedTime: {
    formattedTime: string;
    date: string;
    time: string;
    duration: string;
  } | null;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = observer(({ onClose, selectedTime }) => {
  const [step, setStep] = useState<number>(1);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<any>(null);
  
  const { isLoading, patientId, setIsLoading } = useAppointmentBooking();
  // eslint-disable-next-line 
  const [patient, appointmentType, doctor, clinic, datetime, appointmentId, setData, resetData] = useBookAppointmentStore((state) => [
    state.patient,
    state.appointmentType,
    state.doctor,
    state.clinic,
    state.datetime,
    state.appointmentId,
    state.setData,
    state.resetData,
  ]);

  const { showAlert: showPopUpAlert } = useAlert();

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  const borderColor = isServiceBooking
    ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg
    : "#B92F2F";

  const myPatientsOnly = doctor?.my_patients === "1";
  const displayOnly = doctor?.display_only === "1";
  const isNewPatient = (patient?.is_new_pt ?? '1') === '1';
  const isExistingPatientOfDoctor = patient?.last_seen_did === doctor?.doctor_id;
  
  const [requestConsultMutation] = useRequestConsultMutation({});
  const [registerUserToClinicMutation] = useRegisterUserToClinicMutation();
  const [registerFamilyMemberToClinicMutation] = useRegisterFamilyMemberToClinicMutation();
  const [bannedPatientsQuery,
    // eslint-disable-next-line 
    { loading: bannedPatientsLoading },
  ] = useBannedPatientsLazyQuery();

  const handlePaymentYes = () => setStep(3);
  const handlePaymentNo = () => setStep(1);

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
    setIsLoading(false);
    window.location.reload();
  };

  const handleConfirmAppointment = async () => {
   
    let patientId = null;
    setIsLoading(true);
   
    if (patient?.family_member_key) {

      try {
        const result = await registerFamilyMemberToClinicMutation({
          variables: {
            input: {
              clinic_group_crm: doctor.clinic_group_crm,
              user_fk: doctor.user_fk,
              location_id: doctor.location_id,
              family_member_key: parseInt(patient?.family_member_key),
            },
          },
        });

        patientId = result?.data?.registerFamilyMemberToClinic?.patient_id;
        patientStore.setPatientId(patientId);
      } catch (error) {
        logError('bookAppointmentRegisterError', error);
        console.error("bookAppointmentRegisterError", JSON.stringify(error, null, 2));
      }
    } else {
      try {
        const result = await registerUserToClinicMutation({
          variables: {
            input: {
              clinic_group_crm: clinicStore.currentClinic.clinic_group_crm,
              user_fk: clinicStore.currentClinic.user_fk,
              location_id: clinicStore.currentClinic.location_id,
            },
          },
        });

        patientId = result?.data?.registerUserToClinic?.patient_id;
        patientStore.setPatientId(patientId);

        setIsLoading(false);
      } catch (error) {
        logError('bookAppointmentRegisterError', error);
        console.error("bookAppointmentRegisterError", JSON.stringify(error, null, 2));
      }
    }
    if (
      appointmentTypesStore.selectedAptType?.pre_payment_req === '1' ||
      appointmentTypesStore.selectedAptType?.post_payment_req === '1'
    ) {
      setStep(2);
      return;
    } else {
      handleSubmit();
      return;
    }
  };

  // Create utility functions to handle specific validations
  const validatePatient = (patientId: string, showPopUpAlert: Function) => {
    if (!patientId) {
      showPopUpAlert({
        severity: "error",
        title: "Whoops",
        message: "An error occurred while registering the patient. Please try again."
      });
      return false;
    }
    return true;
  };

  // Main handleSubmit function
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Get appointment type and duration
      const apptype = isServiceBooking 
        ? servicebookingFlowStore?.appointmentData?.appointmentType?.appointment_type_id?.toString()
        : appointmentTypesStore.selectedAptType?.appointment_type_id?.toString();
      
      const duration = selectedTime?.duration?.toString() || "0";
      const patientId = patientStore.currentPatient.id;
      const clinic = clinicStore.currentClinic;
       
      // Validate patient
      if (!validatePatient(patientId, showPopUpAlert)) {
        setIsLoading(false);
        return;
      }

      // Check banned status
      // Check that the patient isn't banned from the clinic
      try {
        const result = await bannedPatientsQuery({
          variables: {
            input: {
              clinic_group_crm: clinic.clinic_group_crm,
              user_fk: clinic.user_fk,
              location_id: clinic.location_id,
              patient_id: patientId,
            },
          },
        });

        const bannedLevel = parseInt(result?.data?.bannedPatients.banned_level || "0");
        if (bannedLevel !== 0) {
          showPopUpAlert({
            severity: "warning",
            title: "Something wrong",
            message: "Please contact the clinic to make an appointment.",
            onClose: () => {
              servicebookingFlowStore.setIsModalOpen(false);
              bookingFlowStore.setIsModalOpen(false);
            },
          });
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.log(error);
      }

    // Validate age
    const patientAge = calculateAgeFromDOB(patientStore?.currentPatient?.dob);

    if (
      !isAgeInRange(patientAge, {
        low_age_min: appointmentTypesStore.selectedAptType?.low_age_min,
        low_age_max: appointmentTypesStore.selectedAptType?.low_age_max,
        upper_age_min: appointmentTypesStore.selectedAptType?.upper_age_min,
        upper_age_max: appointmentTypesStore.selectedAptType?.upper_age_max,
      })
    ) {
      showPopUpAlert({
        severity: "warning",
        title: "Patient Age",
        message: "Patient is out of accepted age range for this Appointment type",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
          bookingFlowStore.setIsModalOpen(false);
        },
      })
      setIsLoading(false);
      return;
    }

    if (isServiceBooking ? (servicebookingFlowStore.appointmentData.appointmentType?.new_patient ?? '0') === '0' : (appointmentTypesStore.selectedAptType?.new_patient ?? '0') === '0' && isNewPatient) {
      showPopUpAlert({
        severity: "warning",
        title: "This Appointment Type Is for Existing Patients Only",
        message: "This is available only to patients who have previously visited this clinic. Please select a different appointment type suitable for new patients.",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
          bookingFlowStore.goToStep(3);
          bookingFlowStore.setActiveStep(3);
        },
      });
      return;
    }

    if (doctor?.no_new_patients === "1" && isNewPatient) {
      showPopUpAlert({
        severity: "warning",
        title: "This Doctor Is Not Accepting New Patients",
        message: "This doctor is currently only seeing existing patients. Please select a different doctor for your appointment.",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
          bookingFlowStore.goToStep(6);
          bookingFlowStore.setActiveStep(6);
        },
      });
      return;
    }

    if (myPatientsOnly && !isExistingPatientOfDoctor) {
      showPopUpAlert({
        severity: "warning",
        title: "This Doctor Is Not Accepting New Patients",
        message: "This doctor is currently only seeing existing patients. Please select a different doctor for your appointment.",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
          bookingFlowStore.goToStep(6);
          bookingFlowStore.setActiveStep(6);
        },
      });
      return;
    }

    if (displayOnly) {
      showPopUpAlert({
        severity: "warning",
        title: "This Doctor Is Not Accepting New Patients",
        message: "This doctor is currently only seeing existing patients. Please select a different doctor for your appointment.",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
          bookingFlowStore.goToStep(6);
          bookingFlowStore.setActiveStep(6);
        },
      });
      return;
    }

    // Ensure that doctor is defined before accessing doctor.doctor_id
    if (!doctor?.doctor_id) {
      showPopUpAlert({
        severity: "warning",
        title: "No doctor",
        message: "No doctor selected. Please select a doctor and try again."
      });
      setIsLoading(false);
      return;
    }

      // Check payment requirements
      if (appointmentTypesStore.selectedAptType?.pre_payment_req === '1' || 
          appointmentTypesStore.selectedAptType?.post_payment_req === '1') {
        setStep(2);
        setIsLoading(false);
        return;
      }
      console.log("Selected Doctor:", doctorsStore.selectedDoctor);
      // Make appointment request
      logger.info("Booking appointment with details:", {
            clinic_group_crm: clinicStore.currentClinic.clinic_group_crm,
            user_fk: clinicStore.currentClinic.user_fk,
            location_id: clinicStore.currentClinic.location_id,
            doctor_id: doctorsStore.selectedDoctor.doctor_id,
            patient_id: patientId,
            patient_name: patientStore.currentPatient?.last_name,
            date: selectedTime?.date,
            time: selectedTime?.time,
            appointment_type_id: apptype,
            duration: duration,
          });
      const result = await requestConsultMutation({
        variables: {
          input: {
            clinic_group_crm: clinicStore.currentClinic.clinic_group_crm,
            user_fk: clinicStore.currentClinic.user_fk,
            location_id: clinicStore.currentClinic.location_id,
            doctor_id: doctorsStore.selectedDoctor.doctor_id,
            patient_id: patientId,
            patient_name: patientStore.currentPatient?.last_name,
            date: selectedTime?.date,
            time: selectedTime?.time,
            appointment_type_id: apptype,
            duration: duration,
          },
        },
      });

      // Track event
      (window as any).gtag('event', isServiceBooking ? 'service_booking_complete' : 'booking_complete', {
        clinic_group_crm: doctor.clinic_group_crm,
        user_fk: doctor.user_fk,
        location_id: doctor.location_id,
      });

      // Update appointment details
      const appointmentDetails = {
        title: `Appointment with ${doctor?.display_name || 'Doctor'}`,
        description: `Appointment for ${patient?.first_name || 'N/A'} ${patient?.last_name || 'N/A'}`,
        startTime: toISOString(new Date(`${selectedTime?.date}T${selectedTime?.time}`)),
        endTime: toISOString(new Date(new Date(`${selectedTime?.date}T${selectedTime?.time}`).getTime() + parseInt(duration) * 60000)),
        location: clinic?.centre_name || 'N/A',
      };

      setData({
        patientId,
        appointmentId: result?.data?.requestConsult?.appointment_id,
      });

      setAppointmentDetails(appointmentDetails);
      bookingFlowStore.setAppointmentDetails(appointmentDetails);
      servicebookingFlowStore.setAppointmentDetails(appointmentDetails);

      setStep(4);

    } catch (error) {
      logError('bookAppointmentConfirmError', error);
      logger.error("bookAppointmentConfirmError", error);
      showPopUpAlert({
        severity: "error",
        title: "Whoops",
        message: "An error occurred while booking the appointment. Please try again.",
        onClose: () => {}
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Main render logic
  return (
    <Box width={{ base: "100%" }} p={4} display="flex" flexDirection="column" alignItems="center">
      {step === 1 && (
        <>
          {(clinic?.confirm_app_info || clinic?.confirm_app_holiday_info) && (
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <AlertIcon boxSize="40px" mr={0} mb={2} />
              <p dangerouslySetInnerHTML={{ __html: clinic?.confirm_app_info || "" }} />
            </Alert>
          )}
          
          <AppointmentDetails
            patient={patientStore.currentPatient}
            selectedTime={selectedTime}
            doctor={doctor}
            clinic={clinic}
          />

          <Button
            bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
            color="#fff"
            width={{ base: "100%", md: "50%" }}
            borderRadius="30px"
            mt="20px"
            onClick={handleConfirmAppointment}
            isLoading={isLoading}
          >
            Confirm Appointment
          </Button>
        </>
      )}

      {step === 2 && (
        <PaymentConfirmation
          appointmentType={appointmentTypesStore.selectedAptType}
          borderColor={borderColor}
          isServiceBooking={isServiceBooking}
          handlePaymentNo={handlePaymentNo}
          handlePaymentYes={handlePaymentYes}
          isPatientValid={!!patientStore.currentPatient?.id}
          servicebookingFlowStore={servicebookingFlowStore}
        />
      )}

      {step === 3 && (
        <PaymentStep
          clinic={clinic}
          patient={patientStore.currentPatient}
          patientId={patientId}
          doctor={doctor}
          locationId={doctor?.location_id}
          pmethod={appointmentTypesStore.selectedAptType?.pre_payment_req === '1' ? '2' : '3'}
          aptypeid={appointmentTypesStore.selectedAptType?.appointment_type_id}
          bid={appointmentDetails?.appointmentId}
          fees={''}
          isSuccessModalOpen={isSuccessModalOpen}
          onSuccessModalClose={closeSuccessModal}
          handleSuccess={() => setStep(4)}
          handleError={() => console.log("error")}
          handlePrevious={handlePaymentNo}
        />
      )}

      {step === 4 && (
        <BookingSuccess
          onClose={closeSuccessModal}
          appointmentDetails={appointmentDetails}
        />
      )}
    </Box>
  );
});

export default ConfirmationStep;